import { FiMic } from 'react-icons/fi';

export const selfIntroductionData = {
  courseId: 'self-introduction',
  courseType: 'speaking-course',
  title: 'Self-Introduction Skills',
  icon: FiMic,
  overview: {
    impact: 'Make a confident, natural first impression in the IELTS exam',
    focus: 'Greeting, personal background, work/study, and why you\'re taking IELTS',
    uses: 'Speaking Part 1 opening + ID check',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'First Impressions',
      content: `<p>The exam begins with an ID check and a brief personal introduction. This isn't scored separately, but it sets the tone for the entire test. A calm, confident start helps you settle in.</p>`,
      points: [
        '<strong>Greeting:</strong> A simple "Good morning" or "Good afternoon" with a smile is perfect.',
        '<strong>Eye contact:</strong> Look at the examiner when speaking—it signals confidence and engagement.',
        '<strong>Body language:</strong> Sit up straight, keep your hands relaxed, and avoid fidgeting.',
        '<strong>Volume and pace:</strong> Speak clearly, not too fast and not too quiet. The examiner needs to hear you.',
        'The ID check ("What\'s your full name?", "Where are you from?") is just verification—answer briefly and naturally.',
      ],
      warning: 'Don\'t memorize a speech. Examiners notice rehearsed introductions immediately, and it creates a negative impression.',
    },
    {
      title: 'Talking About Yourself',
      content: `<p>After the ID check, the examiner may ask general questions about you. Keep answers natural and conversational—like talking to a friendly stranger.</p>`,
      points: [
        '<strong>Name:</strong> "My name is..." or "I\'m..." (both are fine). You can add what people call you: "My friends call me Sam."',
        '<strong>Where you\'re from:</strong> Name the city and add one detail: "I\'m from Lagos—it\'s a really bustling, energetic city."',
        '<strong>An interesting detail:</strong> Mention something memorable: a hobby, a recent experience, or what makes your hometown special.',
        'Keep it to 2-3 sentences. This is a warm-up, not a monologue.',
      ],
      examples: [
        {
          label: 'Natural',
          text: '"I\'m Maria, and I come from a small town near São Paulo. It\'s quite peaceful—surrounded by farms and green hills."',
        },
        {
          label: 'Too robotic',
          text: '"My name is Maria Silva. I was born on January 5th, 1998. I live at 42 Rua da Liberdade. My identification number is..."',
        },
      ],
    },
    {
      title: 'Work/Study Descriptions',
      content: `<p>"Do you work or are you a student?" is one of the most common opening questions. Prepare a flexible, natural answer.</p>`,
      points: [
        '<strong>If you work:</strong> State your job, mention one thing you like (or find challenging), and say how long you\'ve been doing it.',
        '<strong>If you study:</strong> Name your subject, say what year you\'re in, and mention what you enjoy about it.',
        '<strong>Why IELTS:</strong> The examiner may ask why you\'re taking the test. Have a simple, honest reason ready.',
      ],
      examples: [
        {
          label: 'Work',
          text: '"I work as a graphic designer at a small agency. I\'ve been there for about two years, and I really enjoy the creative side of it."',
        },
        {
          label: 'Study',
          text: '"I\'m a third-year law student. It\'s demanding, but I find the case studies fascinating, especially anything related to human rights."',
        },
        {
          label: 'Why IELTS',
          text: '"I\'m taking IELTS because I\'m planning to study in Canada next year. I need a Band 7 for my program."',
        },
      ],
      warning: 'Avoid technical jargon about your field. The examiner isn\'t assessing your professional knowledge—just your English.',
    },
    {
      title: 'Do\'s and Don\'ts',
      content: `<p>A quick reference for making your introduction effective:</p>`,
      table: {
        headers: ['Do', 'Don\'t'],
        rows: [
          ['Smile and make eye contact', 'Look down or stare at the table'],
          ['Speak at a natural, steady pace', 'Rush through a memorized script'],
          ['Give 2-3 sentence answers', 'Answer in just one word'],
          ['Be yourself—use your natural personality', 'Pretend to be someone you\'re not'],
          ['Mention specific details (city name, subject, hobby)', 'Stay vague ("I live somewhere nice")'],
          ['Ask the examiner to repeat if you didn\'t hear', 'Guess what they asked and give a random answer'],
        ],
      },
      points: [
        '<strong>Natural ≠ Casual.</strong> Be friendly and relaxed, but still polite.',
        '<strong>Mistakes are fine.</strong> Self-correct briefly and move on: "I work—sorry, I study at the university."',
        '<strong>Practice flexibility.</strong> The examiner might phrase questions differently than you expect.',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose Natural Introductions',
      type: 'select',
      instructions: 'Choose the introduction that sounds the most natural and appropriate for IELTS.',
      questions: [
        {
          text: 'Which is the best way to state your name?',
          options: [
            '"I\'m Ahmed. Most people call me Amo."',
            '"My full legal name is Ahmed Mohammed Ibrahim Al-Rashid, born in 1999."',
            '"Ahmed."',
          ],
          answer: '"I\'m Ahmed. Most people call me Amo."',
          explanation: 'Short, friendly, and adds a personal touch. The second is too formal; the third is too bare.',
        },
        {
          text: 'Which is the best response to "Where are you from?"',
          options: [
            '"I\'m from Hanoi, the capital of Vietnam. It\'s a busy city with great street food."',
            '"Vietnam."',
            '"I originate from the Socialist Republic of Vietnam, specifically the metropolitan area of Hanoi."',
          ],
          answer: '"I\'m from Hanoi, the capital of Vietnam. It\'s a busy city with great street food."',
          explanation: 'Names the place and adds a vivid detail. It\'s conversational and informative without being too long.',
        },
        {
          text: 'Which is the best answer to "Do you work or study?"',
          options: [
            '"I\'m studying engineering at the moment. I\'m in my second year, and I find the practical projects really interesting."',
            '"Study."',
            '"I am currently enrolled in an undergraduate program in the Faculty of Engineering at the National University."',
          ],
          answer: '"I\'m studying engineering at the moment. I\'m in my second year, and I find the practical projects really interesting."',
          explanation: 'Natural, specific, and well-extended. Gives the examiner clear information without being overly formal.',
        },
        {
          text: 'Which answer to "Why are you taking IELTS?" sounds most natural?',
          options: [
            '"I need it for my master\'s application in the UK. I\'m aiming for Band 7."',
            '"Because I have to."',
            '"The acquisition of an IELTS certification is a prerequisite for my academic endeavours abroad."',
          ],
          answer: '"I need it for my master\'s application in the UK. I\'m aiming for Band 7."',
          explanation: 'Direct, clear, and conversational. It answers the question and adds a concrete goal.',
        },
      ],
    },
    {
      title: 'Complete Self-Introductions',
      type: 'fill-in',
      instructions: 'Fill in the blank to complete each self-introduction naturally.',
      questions: [
        {
          text: '"Good morning. My name is Sara, and I\'m from Casablanca. It\'s a really ___ city."',
          answer: 'vibrant',
          explanation: 'A descriptive adjective adds colour to your introduction. Other good options: "lively", "diverse", "beautiful".',
        },
        {
          text: '"I work as a nurse at a local hospital. I\'ve been doing it for three years, and I particularly enjoy ___ patients."',
          answer: 'helping',
          explanation: 'The sentence needs a verb (gerund form) that explains what you enjoy. "Helping", "caring for", or "working with" all work.',
        },
        {
          text: '"I\'m taking the IELTS because I want to ___ in Australia next year."',
          answer: 'study',
          explanation: 'A clear purpose for taking IELTS. "Study", "work", or "move" are all common and natural.',
        },
        {
          text: '"I\'m Kenji, from Tokyo. My friends call me Ken. In my free time, I enjoy ___, especially on weekends."',
          answer: 'playing tennis',
          explanation: 'A specific hobby makes the introduction memorable. Any concrete activity works: "cooking", "hiking", "playing the guitar".',
        },
      ],
    },
    {
      title: 'Identify What\'s Wrong',
      type: 'select',
      instructions: 'What is the main problem with each introduction?',
      questions: [
        {
          text: '"My-name-is-Ana-Maria-I-come-from-Brazil-I-study-medicine-I-like-reading-and-I-want-to-go-to-England."',
          options: [
            'Sounds memorized — no natural pausing or intonation',
            'Too short',
            'Bad grammar',
          ],
          answer: 'Sounds memorized — no natural pausing or intonation',
          explanation: 'Running everything together without pauses is a clear sign of a rehearsed script. Speak in natural phrase groups.',
        },
        {
          text: '"I\'m John." (and then silence)',
          options: [
            'Too short — missed the chance to show speaking ability',
            'Too formal',
            'Off-topic',
          ],
          answer: 'Too short — missed the chance to show speaking ability',
          explanation: 'Even during the ID check, adding one small detail ("I\'m from London—a great city") shows the examiner you can communicate.',
        },
        {
          text: '"I am a 24-year-old male of Nigerian nationality, holding a Bachelor of Science degree in Computer Engineering, currently residing at Plot 5, Lekki Phase 1, Lagos."',
          options: [
            'Too formal and sounds like a CV — not conversational',
            'Too short',
            'Contains grammar errors',
          ],
          answer: 'Too formal and sounds like a CV — not conversational',
          explanation: 'This reads like a written document, not a spoken introduction. Use conversational language: "I studied computer engineering in Lagos."',
        },
        {
          text: '"Umm... I don\'t really know what to say... I guess I\'m just a normal person... nothing special about me..."',
          options: [
            'Too self-deprecating — lacks confidence and gives no real information',
            'Too long',
            'Too formal',
          ],
          answer: 'Too self-deprecating — lacks confidence and gives no real information',
          explanation: 'Everyone has something to say. State basic facts: your name, where you\'re from, what you do. Keep it simple and positive.',
        },
      ],
    },
    {
      title: 'Write Your Own Introduction',
      type: 'writing',
      instructions: 'Write a natural self-introduction you could use at the start of the IELTS Speaking test.',
      questions: [
        {
          text: 'Write 3-4 sentences introducing yourself: your name, where you\'re from, and one interesting detail about your city or hometown.',
          example: 'I\'m Priya, and I come from Jaipur, which is in the northwest of India. It\'s often called the Pink City because of its stunning old buildings. I really love it there—especially the food markets.',
          tip: 'Name → Place → One vivid detail about the place. Keep it under 20 seconds when spoken aloud.',
        },
        {
          text: 'Write 2-3 sentences about what you do (work or study) and one thing you like about it.',
          example: 'I\'m currently studying business administration at university. I\'m in my final year, which is quite stressful, but I really enjoy the marketing courses because they\'re very practical.',
          tip: 'State what you do → Year or duration → What you enjoy or find interesting.',
        },
        {
          text: 'Write 2 sentences explaining why you\'re taking IELTS and what score you need.',
          example: 'I\'m taking IELTS because I\'ve been accepted to a postgraduate program in Melbourne, and they require a minimum Band 7. I\'m hoping to start next September.',
          tip: 'Keep it factual and specific. Mention the country, program, or job if relevant.',
        },
      ],
    },
  ],
  tips: [
    'Practice your introduction aloud until it feels comfortable, not memorized',
    'Smile and make eye contact — first impressions start before you speak',
    'Speak at your natural pace; don\'t rush to impress',
    'Keep personal details concise — the examiner wants to hear your English, not your biography',
    'If you make a mistake, correct it briefly and move on — it shows good communication skills',
  ],
  dailyPractice: [
    'Introduce yourself to a mirror or camera for 30 seconds — name, home, work/study',
    'Ask a friend to play examiner and ask "Tell me about yourself" in different ways',
    'Record and listen back: Do you sound natural or robotic? Are there awkward pauses?',
    'Practice with the Liwaza AI Speaking partner for real-time feedback',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Self-Introduction Skills: Greeting, stating name and background, talking about work/study, explaining why you\'re taking IELTS. Focus on natural delivery, eye contact, and avoiding memorized scripts. Difficulty 2.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...selfIntroductionData,
      title: selfIntroductionData.title + ' (Foundations)',
      overview: { ...selfIntroductionData.overview, duration: '15-20 minutes' },
      sections: selfIntroductionData.sections.slice(0, Math.max(2, Math.ceil(selfIntroductionData.sections.length / 2))),
      exercises: selfIntroductionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...selfIntroductionData,
      title: selfIntroductionData.title + ' (Advanced)',
      overview: { ...selfIntroductionData.overview, duration: '30-35 minutes' },
      sections: selfIntroductionData.sections,
      exercises: selfIntroductionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(selfIntroductionData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return selfIntroductionData;
}
